import React, { useState, useEffect } from "react";
import app from "../../firebase";
import { getFirestore, collection, getDocs, addDoc, doc, setDoc, query, where } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function ApplicationForm({ onClose }) {
  const [category, setCategory] = useState("");
  const [program, setProgram] = useState("");
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  useEffect(() => {
    const db = getFirestore(app);
    const fetchDepartments = async () => {
      setDepartmentsLoading(true);
      try {
        const snapshot = await getDocs(collection(db, "departments"));
        const deptNames = snapshot.docs.map(doc => doc.data().name);
        setDepartments(deptNames);
      } catch (err) {
        console.error("Error fetching departments:", err);
        setDepartments([]);
      }
      setDepartmentsLoading(false);
    };
    fetchDepartments();
  }, []);

  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [middleNameNA, setMiddleNameNA] = useState(false);
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [grades, setGrades] = useState(null);
  const [picture, setPicture] = useState(null);
  const [birthCert, setBirthCert] = useState(null);
  const [schoolId, setSchoolId] = useState(null);
  const [pictureHardcopy, setPictureHardcopy] = useState(false);
  const [birthCertHardcopy, setBirthCertHardcopy] = useState(false);
  const [schoolIdHardcopy, setSchoolIdHardcopy] = useState(false);
  const [gradesHardcopy, setGradesHardcopy] = useState(false);
  const [howLearned, setHowLearned] = useState("");
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Validation helpers
  const isValidText = (val) => typeof val === "string" && val.trim().length >= 3 && val.trim().length <= 15;
  const isValidMobile = (val) => /^09\d{9}$/.test(val);
  const isValidEmail = (val) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val);
  
  const isValidFile = (file) => {
    if (!file) return false;
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    return allowedTypes.includes(file.type) && file.size <= 5 * 1024 * 1024;
  };

  const getFileErrorMessage = (file) => {
    if (!file) return "";
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSize = 5 * 1024 * 1024;
    
    if (!allowedTypes.includes(file.type)) {
      return "Only JPG, PNG, or PDF files are allowed";
    }
    if (file.size > maxSize) {
      return `File size must be 5MB or below (${(file.size / 1024 / 1024).toFixed(2)}MB)`;
    }
    return "";
  };

  const isValidDOB = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const year = date.getFullYear();
    
    // Check if year is between 1900 and current year
    if (year < 1900 || year > new Date().getFullYear()) return false;
    
    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    
    // If birthday hasn't occurred this year, subtract 1 from age
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age--;
    }
    
    // Must be at least 16 years old
    return age >= 16;
  };

  const getFieldErrorMessage = (fieldName, value) => {
    switch(fieldName) {
      case "category":
        return !value ? "Category is required" : "";
      case "program":
        return !value ? "Program is required" : "";
      case "firstName":
        return !value || value.trim() === "" ? "First name is required" : !isValidText(value) ? "First name must be 3-15 characters" : "";
      case "middleName":
        return !middleNameNA && (!value || value.trim() === "") ? "Middle name is required or check N/A" : !middleNameNA && !isValidText(value) ? "Middle name must be 3-15 characters" : "";
      case "lastName":
        return !value || value.trim() === "" ? "Last name is required" : !isValidText(value) ? "Last name must be 3-15 characters" : "";
      case "gender":
        return !value ? "Gender is required" : "";
      case "dob":
        return !value ? "Date of birth is required" : !isValidDOB(value) ? "You must be at least 16 years old" : "";
      case "mobile":
        return !value || value.trim() === "" ? "Mobile number is required" : !isValidMobile(value) ? "Must start with 09 and be 11 digits" : "";
      case "email":
        return !value || value.trim() === "" ? "Email is required" : !isValidEmail(value) ? "Invalid email format" : "";
      default:
        return "";
    }
  };

  const [errors, setErrors] = useState({});

  // Real-time validation for text fields
  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);
    if (showValidation) {
      const error = getFieldErrorMessage(fieldName, value);
      setErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
    }
  };

  // Check if all fields are valid
  const getMissingFields = () => {
    let missing = [];
    
    if (!category) missing.push("Category");
    if (!program) missing.push("Program");
    if (!firstName || !isValidText(firstName)) missing.push("First Name");
    if (!middleNameNA && (!middleName || !isValidText(middleName))) missing.push("Middle Name");
    if (!lastName || !isValidText(lastName)) missing.push("Last Name");
    if (!gender) missing.push("Gender");
    if (!dob || !isValidDOB(dob)) missing.push("Date of Birth");
    if (!mobile || !isValidMobile(mobile)) missing.push("Mobile Number");
    if (!email || !isValidEmail(email)) missing.push("Email");
    if (!picture && !pictureHardcopy) missing.push("2x2 Picture");
    if (!birthCert && !birthCertHardcopy) missing.push("Birth/Marriage Certificate");
    if (!schoolId && !schoolIdHardcopy) missing.push("School ID/Government ID");
    if (!grades && !gradesHardcopy) missing.push("Copy of Grades");
    
    return missing;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);
    setSubmitMsg("");
    let newErrors = {};
    
    // Validate all fields
    newErrors.category = getFieldErrorMessage("category", category);
    newErrors.program = getFieldErrorMessage("program", program);
    newErrors.firstName = getFieldErrorMessage("firstName", firstName);
    newErrors.middleName = getFieldErrorMessage("middleName", middleName);
    newErrors.lastName = getFieldErrorMessage("lastName", lastName);
    newErrors.gender = getFieldErrorMessage("gender", gender);
    newErrors.dob = getFieldErrorMessage("dob", dob);
    newErrors.mobile = getFieldErrorMessage("mobile", mobile);
    newErrors.email = getFieldErrorMessage("email", email);
    
    // File validation
    if (!picture && !pictureHardcopy) {
      newErrors.picture = 'Provide 2x2 picture or check "I will submit a physical hard copy later"';
    } else if (picture && !isValidFile(picture)) {
      newErrors.picture = getFileErrorMessage(picture);
    } else {
      newErrors.picture = "";
    }
    
    if (!birthCert && !birthCertHardcopy) {
      newErrors.birthCert = 'Provide birth/marriage certificate or check "I will submit a physical hard copy later"';
    } else if (birthCert && !isValidFile(birthCert)) {
      newErrors.birthCert = getFileErrorMessage(birthCert);
    } else {
      newErrors.birthCert = "";
    }
    
    if (!schoolId && !schoolIdHardcopy) {
      newErrors.schoolId = 'Provide school ID/government ID or check "I will submit a physical hard copy later"';
    } else if (schoolId && !isValidFile(schoolId)) {
      newErrors.schoolId = getFileErrorMessage(schoolId);
    } else {
      newErrors.schoolId = "";
    }
    
    if (!grades && !gradesHardcopy) {
      newErrors.grades = 'Provide copy of grades or check "I will submit a physical hard copy later"';
    } else if (grades && !isValidFile(grades)) {
      newErrors.grades = getFileErrorMessage(grades);
    } else {
      newErrors.grades = "";
    }
    
    // Filter out empty error messages
    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, v]) => v !== "")
    );
    
    setErrors(filteredErrors);
    
    // If there are errors, show missing fields summary
    if (Object.keys(filteredErrors).length > 0) {
      const missing = getMissingFields();
      setSubmitMsg(`❌ Missing or invalid fields: ${missing.join(", ")}`);
      return;
    }

    setSubmitting(true);
    
    try {
      const db = getFirestore(app);
      // Check for unique email
      const q = query(collection(db, "students"), where("email", "==", email));
      const existing = await getDocs(q);
      if (!existing.empty) {
        setErrors(prev => ({ ...prev, email: "Email already exists in the system" }));
        setSubmitMsg("❌ Email already exists. Please use a different email.");
        setSubmitting(false);
        return;
      }
      const storage = getStorage(app);
      // Create a new student doc to get the ID
      const studentRef = doc(collection(db, "students"));
      const studentId = studentRef.id;

      // Helper to upload a file and get its download URL
      const uploadAndGetUrl = async (file, field) => {
        if (!file) return "";
        const storageRef = ref(storage, `students_uploads/${studentId}/${field}_${file.name}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      };

      const [pictureUrl, birthCertUrl, schoolIdUrl, gradesUrl] = await Promise.all([
        picture ? uploadAndGetUrl(picture, "picture") : "",
        birthCert ? uploadAndGetUrl(birthCert, "birthCert") : "",
        schoolId ? uploadAndGetUrl(schoolId, "schoolId") : "",
        grades ? uploadAndGetUrl(grades, "grades") : "",
      ]);

      await setDoc(studentRef, {
        category,
        program,
        firstName,
        middleName,
        middleNameNA,
        lastName,
        gender,
        dob,
        mobile,
        email,
        grades: gradesUrl || "",
        picture: pictureUrl || "",
        birthCert: birthCertUrl || "",
        schoolId: schoolIdUrl || "",
        gradesHardcopy: !!gradesHardcopy,
        pictureHardcopy: !!pictureHardcopy,
        birthCertHardcopy: !!birthCertHardcopy,
        schoolIdHardcopy: !!schoolIdHardcopy,
        createdAt: new Date().toISOString(),
      });
      setSubmitMsg("✅ Student Application Saved! Please wait for your Email for your Login Credentials.");
      setErrors({});
      setShowValidation(false);
    } catch (err) {
      console.error("Error saving application:", err);
      setSubmitMsg("❌ Error saving application. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div style={{
      minHeight: "100vh",
      width: "100vw",
      background: "linear-gradient(90deg, #009966 0%, #155c2b 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <div style={{
        maxWidth: 900,
        width: "calc(100% - 24px)",
        minHeight: 650,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
        padding: 32,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
      }}>
        <h2 style={{ fontWeight: 700, marginBottom: 24, textAlign: "center" }}>Application</h2>
        <form onSubmit={handleSubmit}>
          {/* Category & Program */}
          <div className="application-grid application-grid-two" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 24, marginBottom: 24 }}>
            <div>
              <label style={{ fontWeight: 600 }}>Category*</label>
              <select 
                value={category} 
                onChange={e => handleFieldChange("category", e.target.value, setCategory)} 
                style={{ width: "100%", padding: 10, marginTop: 6, border: errors.category ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
              >
                <option value="">Select Category</option>
                <option value="New Student">New Student</option>
                <option value="Transferee">Transferee</option>
              </select>
              {showValidation && errors.category && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.category}</span>}
            </div>
            <div>
              <label style={{ fontWeight: 600 }}>Program*</label>
              {departmentsLoading ? (
                <div style={{ padding: 10, color: '#888' }}>Loading departments...</div>
              ) : (
                <select 
                  value={program} 
                  onChange={e => handleFieldChange("program", e.target.value, setProgram)} 
                  style={{ width: "100%", padding: 10, marginTop: 6, border: errors.program ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
                >
                  <option value="">Select Program</option>
                  {departments.length === 0 ? (
                    <option value="" disabled>No departments found</option>
                  ) : (
                    departments.map((dept, idx) => (
                      <option key={idx} value={dept}>{dept}</option>
                    ))
                  )}
                </select>
              )}
              {showValidation && errors.program && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.program}</span>}
            </div>
          </div>

          {/* Name fields */}
          <div className="application-grid application-grid-three" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24, marginBottom: 24 }}>
            <div>
              <label style={{ fontWeight: 600 }}>First Name*</label>
              <input
                type="text"
                value={firstName}
                maxLength={15}
                onChange={e => {
                  let val = e.target.value.replace(/[^a-zA-Z\s]/g, "").slice(0, 15);
                  val = val.replace(/\b\w/g, c => c.toUpperCase());
                  handleFieldChange("firstName", val, setFirstName);
                }}
                style={{ width: "100%", padding: 10, marginTop: 6, border: errors.firstName ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
              />
              {showValidation && errors.firstName && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.firstName}</span>}
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <label style={{ fontWeight: 600 }}>Middle Name</label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 4, fontSize: 12, lineHeight: 1.2, fontWeight: 400, marginBottom: 0, textAlign: "left" }}>
                  <input type="checkbox" checked={middleNameNA} onChange={e => {
                    setMiddleNameNA(e.target.checked);
                    if (e.target.checked) {
                      setErrors(prev => ({ ...prev, middleName: "" }));
                      setMiddleName("");
                    }
                  }} /> Not applicable
                </label>
              </div>
              <input
                type="text"
                value={middleName}
                maxLength={15}
                onChange={e => {
                  let val = e.target.value.replace(/[^a-zA-Z\s]/g, "").slice(0, 15);
                  val = val.replace(/\b\w/g, c => c.toUpperCase());
                  handleFieldChange("middleName", val, setMiddleName);
                }}
                style={{ width: "100%", padding: 10, marginTop: 6, border: errors.middleName ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
                disabled={middleNameNA}
              />
              {showValidation && errors.middleName && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.middleName}</span>}
            </div>
            <div>
              <label style={{ fontWeight: 600 }}>Last Name*</label>
              <input
                type="text"
                value={lastName}
                maxLength={15}
                onChange={e => {
                  let val = e.target.value.replace(/[^a-zA-Z\s]/g, "").slice(0, 15);
                  val = val.replace(/\b\w/g, c => c.toUpperCase());
                  handleFieldChange("lastName", val, setLastName);
                }}
                style={{ width: "100%", padding: 10, marginTop: 6, border: errors.lastName ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
              />
              {showValidation && errors.lastName && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.lastName}</span>}
            </div>
          </div>

          {/* Gender, DOB, Mobile */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24, marginBottom: 24 }}>
            <div>
              <label style={{ fontWeight: 600 }}>Gender*</label>
              <select 
                value={gender} 
                onChange={e => handleFieldChange("gender", e.target.value, setGender)} 
                style={{ width: "100%", padding: 10, marginTop: 6, border: errors.gender ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              {showValidation && errors.gender && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.gender}</span>}
            </div>
            <div>
              <label style={{ fontWeight: 600 }}>Date of Birth*</label>
              <input 
                type="date" 
                value={dob} 
                onChange={e => handleFieldChange("dob", e.target.value, setDob)} 
                min="1900-01-01"
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
                style={{ width: "100%", padding: 10, marginTop: 6, border: errors.dob ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
              />
              {showValidation && errors.dob && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.dob}</span>}
            </div>
            <div>
              <label style={{ fontWeight: 600 }}>Mobile Number*</label>
              <input
                type="text"
                value={mobile}
                maxLength={11}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 11);
                  handleFieldChange("mobile", val, setMobile);
                }}
                placeholder="09XXXXXXXXX"
                style={{ width: "100%", padding: 10, marginTop: 6, border: errors.mobile ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
              />
              {showValidation && errors.mobile && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.mobile}</span>}
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 600 }}>Email*</label>
            <input 
              type="email" 
              value={email} 
              onChange={e => handleFieldChange("email", e.target.value, setEmail)} 
              placeholder="example@email.com"
              style={{ width: "100%", padding: 10, marginTop: 6, border: errors.email ? "2px solid red" : "1px solid #ccc", borderRadius: 4 }}
            />
            {showValidation && errors.email && <span style={{ color: "red", fontSize: 12, marginTop: 4, display: "block" }}>⚠ {errors.email}</span>}
          </div>

          <hr style={{ margin: "32px 0" }} />
          <div style={{ marginBottom: 50, fontSize: 15 }}>
            The attachments required below will be used only for verifying the applicant's identity and qualifications.<br />
            Admission requirements should be submitted in hard copy.<br />
            <strong>Upload attachments accept JPG, PNG, PDF type (filesize: 5MB and below)</strong>
          </div>

          {/* Attachments - 3 columns */}
          <div className="application-grid application-grid-three" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24, marginBottom: 0 }}>
            <div style={{ display: "flex", flexDirection: "column", marginBottom: 24 }}>
              <label style={{ display: "block", fontWeight: 600, lineHeight: 1.25 }}>2x2 Picture*</label>
              {showValidation && errors.picture && <span style={{ color: "red", fontSize: 12 }}>⚠ {errors.picture}</span>}
              <input 
                type="file" 
                accept=".jpg,.jpeg,.png,.pdf" 
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    const error = getFileErrorMessage(file);
                    if (error) {
                      setErrors(prev => ({ ...prev, picture: error }));
                      setPicture(null);
                    } else {
                      setPicture(file);
                      setErrors(prev => ({ ...prev, picture: "" }));
                    }
                  }
                }} 
                style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", marginTop: 6, border: errors.picture ? "2px solid red" : "1px solid #ccc", padding: 8, borderRadius: 4 }} 
                disabled={pictureHardcopy} 
              />
              <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>In formal attire and white background</span>
              <label style={{ marginTop: 8, fontSize: 12 }}>
                <input type="checkbox" checked={pictureHardcopy} onChange={e => { 
                  setPictureHardcopy(e.target.checked); 
                  if (e.target.checked) {
                    setPicture(null);
                    setErrors(prev => ({ ...prev, picture: "" }));
                  }
                }} /> I will submit a physical hard copy later
              </label>
            </div>

            <div style={{ display: "flex", flexDirection: "column", marginBottom: 24 }}>
              <label style={{ display: "block", fontWeight: 600, lineHeight: 1.25 }}>Birth/Marriage Certificate*</label>
              {showValidation && errors.birthCert && <span style={{ color: "red", fontSize: 12 }}>⚠ {errors.birthCert}</span>}
              <input 
                type="file" 
                accept=".jpg,.jpeg,.png,.pdf" 
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    const error = getFileErrorMessage(file);
                    if (error) {
                      setErrors(prev => ({ ...prev, birthCert: error }));
                      setBirthCert(null);
                    } else {
                      setBirthCert(file);
                      setErrors(prev => ({ ...prev, birthCert: "" }));
                    }
                  }
                }} 
                style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", marginTop: 6, border: errors.birthCert ? "2px solid red" : "1px solid #ccc", padding: 8, borderRadius: 4 }} 
                disabled={birthCertHardcopy} 
              />
              <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>PSA Authenticated</span>
              <label style={{ marginTop: 8, fontSize: 12 }}>
                <input type="checkbox" checked={birthCertHardcopy} onChange={e => { 
                  setBirthCertHardcopy(e.target.checked); 
                  if (e.target.checked) {
                    setBirthCert(null);
                    setErrors(prev => ({ ...prev, birthCert: "" }));
                  }
                }} /> I will submit a physical hard copy later
              </label>
            </div>

            <div style={{ display: "flex", flexDirection: "column", marginBottom: 24 }}>
              <label style={{ display: "block", fontWeight: 600, lineHeight: 1.25, overflowWrap: "anywhere" }}>Latest School ID/Government ID*</label>
              {showValidation && errors.schoolId && <span style={{ color: "red", fontSize: 12 }}>⚠ {errors.schoolId}</span>}
              <input 
                type="file" 
                accept=".jpg,.jpeg,.png,.pdf" 
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    const error = getFileErrorMessage(file);
                    if (error) {
                      setErrors(prev => ({ ...prev, schoolId: error }));
                      setSchoolId(null);
                    } else {
                      setSchoolId(file);
                      setErrors(prev => ({ ...prev, schoolId: "" }));
                    }
                  }
                }} 
                style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", marginTop: 6, border: errors.schoolId ? "2px solid red" : "1px solid #ccc", padding: 8, borderRadius: 4 }} 
                disabled={schoolIdHardcopy} 
              />
              <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>from the last school attended</span>
              <label style={{ marginTop: 8, fontSize: 12 }}>
                <input type="checkbox" checked={schoolIdHardcopy} onChange={e => { 
                  setSchoolIdHardcopy(e.target.checked); 
                  if (e.target.checked) {
                    setSchoolId(null);
                    setErrors(prev => ({ ...prev, schoolId: "" }));
                  }
                }} /> I will submit a physical hard copy later
              </label>
            </div>
          </div>

          {/* Grades - full width */}
          <div style={{ display: "flex", flexDirection: "column", marginBottom: 24 }}>
            <label style={{ fontWeight: 600 }}>Copy of Grades*</label>
            {showValidation && errors.grades && <span style={{ color: "red", fontSize: 12 }}>⚠ {errors.grades}</span>}
            <input 
              type="file" 
              accept=".jpg,.jpeg,.png,.pdf" 
              onChange={e => {
                const file = e.target.files[0];
                if (file) {
                  const error = getFileErrorMessage(file);
                  if (error) {
                    setErrors(prev => ({ ...prev, grades: error }));
                    setGrades(null);
                  } else {
                    setGrades(file);
                    setErrors(prev => ({ ...prev, grades: "" }));
                  }
                }
              }} 
              style={{ marginTop: 6, width: "100%", border: errors.grades ? "2px solid red" : "1px solid #ccc", padding: 8, borderRadius: 4 }} 
              disabled={gradesHardcopy} 
            />           
            <span style={{ fontSize: 12, color: "#888", marginTop: 4 }}>Report Card/Transcript of Records</span>
            <label style={{ marginTop: 8, fontSize: 12 }}>
              <input type="checkbox" checked={gradesHardcopy} onChange={e => { 
                setGradesHardcopy(e.target.checked); 
                if (e.target.checked) {
                  setGrades(null);
                  setErrors(prev => ({ ...prev, grades: "" }));
                }
              }} /> I will submit a physical hard copy later
            </label>
          </div>

          <hr style={{ margin: "32px 0" }} />

          <div style={{ fontSize: 12, color: "#888", marginBottom: 18 }}>*indicates required field</div>
          
          {submitMsg && (
            <div style={{ 
              color: submitMsg.includes("❌") ? "#d32f2f" : "#388e3c", 
              backgroundColor: submitMsg.includes("❌") ? "#ffebee" : "#e8f5e9",
              border: submitMsg.includes("❌") ? "1px solid #ef5350" : "1px solid #81c784",
              marginBottom: 18, 
              padding: 12,
              borderRadius: 4,
              fontWeight: 500 
            }}>
              {submitMsg}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
            <button
              type="button"
              style={{ background: "#fff", color: "#e67e22", border: "1px solid #e67e22", borderRadius: 6, padding: "10px 22px", fontWeight: 500, fontSize: 15, cursor: "pointer" }}
              onClick={() => {
                setCategory("");
                setProgram("");
                setFirstName("");
                setMiddleName("");
                setMiddleNameNA(false);
                setLastName("");
                setGender("");
                setDob("");
                setMobile("");
                setEmail("");
                setGrades(null);
                setPicture(null);
                setBirthCert(null);
                setSchoolId(null);
                setPictureHardcopy(false);
                setBirthCertHardcopy(false);
                setSchoolIdHardcopy(false);
                setGradesHardcopy(false);
                setHowLearned("");
                setSubmitMsg("");
                setErrors({});
                setShowValidation(false);
              }}
            >CLEAR</button>
            <button type="button" style={{ background: "#f5f5f5", color: "#009966", border: "1px solid #e0e0e0", borderRadius: 6, padding: "10px 22px", fontWeight: 500, fontSize: 15, cursor: "pointer" }} onClick={onClose}>CLOSE</button>
            <button
              type="submit"
              disabled={submitting}
              style={{ background: submitting ? "#ccc" : "#009966", color: "#fff", border: "none", borderRadius: 6, padding: "10px 22px", fontWeight: 500, fontSize: 15, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? "Saving..." : "SUBMIT"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
